import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import { IconButton, Link, Stack, Typography } from "@mui/material";
import type { GridApiPremium, GridColDef, GridDataSource } from "@mui/x-data-grid-premium";
import { parseISO } from "date-fns";
import type { RefObject } from "react";
import { useCallback, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import { GRID_DEFAULT_PAGE_SIZE } from "@entrilia/shared/components/grid/gridHelper";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { logError } from "@entrilia/shared/logging";
import { distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import { formatDateTime } from "@entrilia/shared/utilities/dateUtils";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import type { DataImportInfo } from "../../../../api/types/dataImportTypes";
import { dataImportTargetCaptionMap } from "../../../../utilities/enumCaptions";
import ImportDataContext from "../ImportDataContext";
import ImportDataStatusTag from "../ImportDataStatusTag";
import ImportSourceLabel from "../ImportSourceLabel";
import { getDataImportSubpath, isImportDeletable } from "../importDataStateHelper";

const DeleteDataImportButton = ({ row }: { row: DataImportInfo }) => {
  const { onDeleteImport } = useDefinedContext(ImportDataContext);

  if (!isImportDeletable(row.state)) {
    return null;
  }

  return (
    <IconButton color="error" onClick={() => onDeleteImport(row)}>
      <DeleteIcon />
    </IconButton>
  );
};

const columnDefinitions: GridColDef<DataImportInfo>[] = [
  {
    field: "description",
    headerName: "Name",
    minWidth: 200,
    flex: 1,
    renderCell: ({ row }) => {
      return (
        <GridTextHoverLink
          to={row.source === "Passthrough" ? undefined : getDataImportSubpath(row)}
          text={row.description || row.sourceDescription}
        />
      );
    },
  },
  {
    field: "source",
    headerName: "Source",
    minWidth: 160,
    flex: 1,
    renderCell: ({ row }) => <ImportSourceLabel dataImport={row} />,
  },
  {
    field: "targetEntityName",
    headerName: "Entity",
    minWidth: 160,
    flex: 1,
    renderCell: ({ row }) => {
      if (row.targetEntityName) {
        return <Typography noWrap>{row.targetEntityName}</Typography>;
      }

      return (
        <Stack sx={{ maxWidth: "100%" }}>
          {distinctBy(row.files, (f) => f.importObjectTypeTarget).map(({ importObjectTypeTarget }) => (
            <Typography key={importObjectTypeTarget} noWrap>
              {autoFormatCamelCase(importObjectTypeTarget)}
            </Typography>
          ))}
        </Stack>
      );
    },
  },
  {
    field: "importData",
    headerName: "Import Data",
    minWidth: 200,
    flex: 1,
    cellClassName: ({ row }) =>
      row.importData.some((d) => !!d.url && ["AwaitingApplication", "Ready"].includes(row.state))
        ? "highlighted-action"
        : "",
    renderCell: ({ row }) => {
      if (row.importData.length > 0) {
        return (
          <Stack sx={{ maxWidth: "100%" }}>
            {row.importData.map((data, index) =>
              data.url && ["AwaitingApplication", "Ready"].includes(row.state) ? (
                <Link
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={index}
                  href={data.url}
                  underline="none"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "inherit" }}
                >
                  <Typography noWrap>{data.category}</Typography>
                </Link>
              ) : (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <Typography key={index} noWrap color="textDisabled">
                  {data.category}
                </Typography>
              )
            )}
          </Stack>
        );
      }

      return (
        <Stack sx={{ maxWidth: "100%" }}>
          {distinctBy(row.files, (f) => f.importTypeTarget).map(({ importTypeTarget }) => (
            <Typography key={importTypeTarget} noWrap>
              {dataImportTargetCaptionMap[importTypeTarget]}
            </Typography>
          ))}
        </Stack>
      );
    },
  },
  {
    field: "state",
    headerName: "State",
    minWidth: 180,
    width: 180,
    renderCell: ({ row }) => <ImportDataStatusTag dataImportState={row.state} />,
  },
  {
    field: "createdByUserName",
    headerName: "Created by",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "createdAt",
    headerName: "Created at",
    minWidth: 180,
    width: 180,
    valueGetter: (_, row) => {
      const createdDate = parseISO(row.createdAt);
      return createdDate;
    },
    valueFormatter: (value) => formatDateTime(value),
  },
  {
    field: "actions",
    cellClassName: "grid-row-actions",
    headerName: "Actions",
    minWidth: 72,
    width: 72,
    renderCell: DeleteDataImportButton,
  },
];

interface Props {
  dataSource: GridDataSource;
  apiRef?: RefObject<GridApiPremium | null>;
}

const ImportDataGrid = ({ dataSource, apiRef }: Props) => {
  const [dataSourceError, setDataSourceError] = useState<Error>();

  const handleDataSourceError = useCallback((error: Error) => {
    logError(error, "[ImportDataGrid]");
    setDataSourceError(error);
  }, []);

  if (dataSourceError) {
    return <DataLoadingFailed title="Loading imports failed" />;
  }

  return (
    <DataGrid<DataImportInfo>
      apiRef={apiRef}
      columns={columnDefinitions}
      lazyLoading
      dataSource={dataSource}
      paginationModel={{ page: 0, pageSize: GRID_DEFAULT_PAGE_SIZE }}
      onDataSourceError={handleDataSourceError}
      noRowsText="No imports"
      disableColumnReorder
      disableColumnSorting
    />
  );
};

export default ImportDataGrid;
